// frontend/src/components/Tooltip.jsx
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

export default function Tooltip({ text, children, delay = 500 }) {
  const [visible, setVisible] = useState(false);
  const childRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [adjustedCoords, setAdjustedCoords] = useState({ top: 0, left: 0 });

  // Ajuster la position du tooltip pour qu'il reste dans les limites du viewport
  useEffect(() => {
    if (visible && tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let newLeft = coords.left;
      let newTop = coords.top + 8;
      
      // Ajuster horizontalement si le tooltip dépasse à droite
      if (newLeft + tooltipRect.width / 2 > viewportWidth - 10) {
        newLeft = viewportWidth - tooltipRect.width / 2 - 10;
      }
      // Ajuster horizontalement si le tooltip dépasse à gauche
      if (newLeft - tooltipRect.width / 2 < 10) {
        newLeft = tooltipRect.width / 2 + 10;
      }
      
      // Ajuster verticalement si le tooltip dépasse en bas
      if (newTop + tooltipRect.height > viewportHeight - 10) {
        // Afficher au-dessus de l'élément
        newTop = coords.top - tooltipRect.height - 8;
      }
      
      setAdjustedCoords({ top: newTop, left: newLeft });
    }
  }, [visible, coords]);

  const showTooltip = () => {
    if (childRef.current) {
      const rect = childRef.current.getBoundingClientRect();
      setCoords({
        left: rect.left + rect.width / 2,
        top: rect.bottom,
      });
    }
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
  };

  const tooltipStyle = {
    position: 'fixed',
    top: adjustedCoords.top,
    left: adjustedCoords.left,
    transform: 'translateX(-50%)',
    backgroundColor: '#333',
    color: '#fff',
    padding: '6px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    whiteSpace: 'normal',
    maxWidth: '400px',
    wordBreak: 'break-word',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out',
    pointerEvents: 'none',
    zIndex: 10000,
  };

  // Clonage de l'élément enfant pour y injecter les handlers
  const childWithHandlers = React.cloneElement(children, {
    ref: childRef,
    onMouseEnter: (e) => {
      if (children.props.onMouseEnter) {
        children.props.onMouseEnter(e);
      }
      showTooltip();
    },
    onMouseLeave: (e) => {
      if (children.props.onMouseLeave) {
        children.props.onMouseLeave(e);
      }
      hideTooltip();
    },
  });

  return (
    <>
      {childWithHandlers}
      {ReactDOM.createPortal(
        <div ref={tooltipRef} style={tooltipStyle}>{text}</div>,
        document.body
      )}
    </>
  );
}
